import type {
  VoucherGetByCodeRequest,
  VoucherGetByCodeResponse,
} from '@diamond/shared/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import shallow from 'zustand/shallow';

import { getVoucherByCode } from '../queries';
import { useAuthStore, useCartStore, useShipToStore } from '../stores';
import { useCartTotal } from './useCartTotal';

export const VOUCHER_BY_CODE_KEY = 'VoucherByCode';
const CACHE_STALE_TIME = 5 * 60 * 1000;

export const useVoucherByCode = () => {
  const accessToken = useAuthStore((state) => state.access_token);
  const warehouse = useShipToStore((s) => s.selectedShipToId, shallow);
  const queryClient = useQueryClient();

  const cartItems = useCartStore((state) => state.cartItems, shallow);
  const cartRecord = useCartStore((state) => state.cartRecord, shallow);

  const { data: cartTotal } = useCartTotal();

  const buildVoucherPayload = useCallback(
    (voucherCode: string): VoucherGetByCodeRequest => {
      const normalizedCode = voucherCode.trim().toUpperCase();
      if (!normalizedCode) {
        throw new Error('Kode voucher kosong - mohon masukkan kode');
      }

      if (!warehouse) {
        throw new Error(
          'Pilih alamat pengiriman terlebih dahulu sebelum menggunakan voucher'
        );
      }

      const voucherCartItems = cartItems
        .map((id) => {
          const itemCode = cartRecord[id]?.product?.item_code;
          const quantity = cartRecord[id]?.quantity ?? 0;

          if (!itemCode) {
            return null;
          }

          return {
            product_item_code: itemCode,
            quantity,
          };
        })
        .filter(Boolean) as VoucherGetByCodeRequest['items'];

      return {
        warehouse,
        voucher_code: normalizedCode,
        cart_total_amount: cartTotal?.final_total ?? 0,
        items: voucherCartItems,
      };
    },
    [cartItems, cartRecord, cartTotal?.final_total, warehouse]
  );

  const fetchVoucherByCode = useCallback(
    async (voucherCode: string) => {
      if (!accessToken) {
        throw new Error('Sesi tidak ditemukan - silakan masuk kembali');
      }

      const payload = buildVoucherPayload(voucherCode);

      return getVoucherByCode(accessToken, payload);
    },
    [buildVoucherPayload, accessToken]
  );

  const fetchWithCache = async (
    voucherCode: string
  ): Promise<VoucherGetByCodeResponse> => {
    const normalizedCode = voucherCode.trim().toUpperCase();
    const queryKey = [VOUCHER_BY_CODE_KEY, normalizedCode];
    const cachedData =
      queryClient.getQueryData<VoucherGetByCodeResponse>(queryKey);
    const cacheState = queryClient.getQueryState(queryKey);

    if (cachedData && cacheState) {
      const now = Date.now();
      const dataAge = now - (cacheState.dataUpdatedAt || 0);
      const isStale = dataAge > CACHE_STALE_TIME;

      if (!isStale) {
        return cachedData;
      }
    }

    const data = await fetchVoucherByCode(normalizedCode);
    queryClient.setQueryData(queryKey, data);

    return data;
  };

  return {
    fetchWithCache,
  };
};
